/* screens/crm/LeadTransferModal.jsx — перенос лида в другую воронку.
   Режимы: «Переместить» (лид переезжает целиком) и «Скопировать» (лид остаётся
   в текущей воронке + создаётся копия в целевой — «и то, и то»). Причина обязательна
   (как при перемещении по этапам) и пишется в историю/заметку. */
import React from 'react';
import { Button } from '../../components/ui/Button.jsx';
import { Field, Textarea } from '../../components/ui/Field.jsx';
import { Select } from '../../components/ui/Select.jsx';
import { Segmented } from '../../components/ui/Field.jsx';
import { Modal } from '../../components/ui/Modal.jsx';

const { useState, useEffect, useMemo } = React;

function LeadTransferModal({ open, lead, pipelines = [], presets = [], onCancel, onConfirm }) {
  const [mode, setMode] = useState('move');       // 'move' | 'copy'
  const [pipelineId, setPipelineId] = useState('');
  const [stageId, setStageId] = useState('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');
  const [saving, setSaving] = useState(false);

  // Целевые воронки — все, кроме текущей (перенос «между» воронками).
  const targets = useMemo(
    () => pipelines.filter((p) => p.id !== lead?.pipeline), [pipelines, lead?.pipeline]);

  useEffect(() => {
    if (open) {
      setMode('move'); setReason(''); setErr(''); setSaving(false);
      const first = targets[0];
      setPipelineId(first ? String(first.id) : '');
      setStageId('');   // пусто → входной этап целевой воронки
    }
  }, [open, lead?.id]); // eslint-disable-line

  const target = targets.find((p) => String(p.id) === String(pipelineId)) || null;
  const stageOptions = [
    { value: '', label: 'Входной этап' },
    ...((target?.stages || []).map((s) => ({ value: String(s.id), label: s.name }))),
  ];

  const submit = async () => {
    if (!pipelineId) { setErr('Выберите воронку.'); return; }
    const v = reason.trim();
    if (!v) { setErr('Укажите причину переноса.'); return; }
    setSaving(true);
    try {
      await onConfirm({
        to_pipeline: Number(pipelineId),
        to_stage: stageId ? Number(stageId) : null,
        reason: v,
        copy: mode === 'copy',
      });
    } catch (e) {
      setErr(e?.detail || e?.message || 'Не удалось перенести лид.');
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={saving ? undefined : onCancel}
      title="Перенести в другую воронку"
      size="sm"
      autoFocusSelector="textarea"
      footer={<>
        <Button variant="ghost" onClick={onCancel} disabled={saving}>Отмена</Button>
        <Button variant="primary" onClick={submit} loading={saving} disabled={!targets.length}>
          {mode === 'copy' ? 'Скопировать' : 'Переместить'}
        </Button>
      </>}
    >
      <div className="stack stack-12">
        {targets.length === 0 ? (
          <p className="text-dim">Других воронок нет. Создайте ещё одну воронку в конструкторе.</p>
        ) : (
          <>
            <Field label="Что сделать">
              <Segmented value={mode} onChange={setMode} ariaLabel="Режим переноса"
                options={[
                  { value: 'move', label: 'Переместить' },
                  { value: 'copy', label: 'Скопировать (в обе)' },
                ]} />
            </Field>
            <p className="t-caption text-mute">
              {mode === 'copy'
                ? 'Лид останется в текущей воронке, а в целевой появится его копия (контакты, сумма, ответственный, доп. поля).'
                : 'Лид целиком переедет в выбранную воронку. История сохранится.'}
            </p>
            <Field label="Воронка" required>
              <Select value={pipelineId} onChange={(v) => { setPipelineId(v); setStageId(''); setErr(''); }}
                options={targets.map((p) => ({ value: String(p.id), label: p.name }))}
                placeholder="Выберите воронку" searchable={targets.length > 6} />
            </Field>
            <Field label="Этап">
              <Select value={stageId} onChange={(v) => { setStageId(v); setErr(''); }}
                options={stageOptions} placeholder="Входной этап" searchable={stageOptions.length > 6} />
            </Field>
            <Field label="Почему лид переносится?" error={err} required>
              <Textarea value={reason} onChange={(e) => { setReason(e.target.value); setErr(''); }}
                minRows={3} maxRows={8} invalid={!!err}
                placeholder="Например: клиент заинтересовался другим продуктом…" />
            </Field>
            {presets.length > 0 && (
              <div className="crm-reason-chips">
                {presets.map((p) => (
                  <button key={p} type="button" className="crm-chip"
                    onClick={() => { setReason(p); setErr(''); }}>{p}</button>
                ))}
              </div>
            )}
            <p className="t-caption text-mute">Причина сохранится в истории лида без возможности удаления.</p>
          </>
        )}
      </div>
    </Modal>
  );
}

export { LeadTransferModal };
